import { CONSULTATION_TYPE } from './constants';

// Template section definition as stored on the server
// When backend API is ready, replace with API-fetched data
export type TemplateSectionDefinition = {
  code: string;
  title: string;
  supercategory?: string;
  instructions?: string;
};

export type TemplateDefinition = {
  type: string;
  sections: TemplateSectionDefinition[];
};

const dentalSections: TemplateSectionDefinition[] = [
  {
    code: 'patient_name',
    supercategory: 'General Information',
    title: 'Patient Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'patient_date_of_birth',
    supercategory: 'General Information',
    title: 'Patient Date of Birth',
    instructions:
      "ISO format. Empty string if not mentioned. For example '1990-01-01' or ''",
  },
  {
    code: 'doctor_name',
    supercategory: 'General Information',
    title: 'Doctor Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'patient_representative_name',
    supercategory: 'General Information',
    title: 'Patient Representative Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'patient_problem',
    supercategory: 'Complaints',
    title: 'Patient Problem',
  },
  {
    code: 'patient_idea_of_treatment',
    supercategory: 'Complaints',
    title: 'Patient Idea of Treatment',
  },
  {
    code: 'patient_expectation_of_treatment',
    supercategory: 'Complaints',
    title: 'Patient Expectation of Treatment',
  },
  {
    code: 'patient_concerns_of_treatment',
    supercategory: 'Complaints',
    title: 'Patient Concerns of Treatment',
  },
  {
    code: 'patient_feelings',
    supercategory: 'Complaints',
    title: 'Patient Feelings',
  },
  { code: 'pain_evaluation', title: 'Pain Evaluation (0-10)' },
  {
    code: 'past_and_concomitant_diseases',
    title: 'Past and Concomitant Diseases',
  },
  {
    code: 'medications_and_drugs_taken_by_the_patient',
    title: 'Medications and Drugs Taken by the Patient',
  },
  {
    code: 'development_of_current_disease',
    title: 'Development of Current Disease',
  },
  { code: 'external_examination', title: 'External Examination' },
  { code: 'condition_of_oral_mucosa', title: 'Condition of Oral Mucosa' },
  { code: 'status_localis', title: 'Status Localis' },
  { code: 'x_ray_examination', title: 'X-Ray Examination' },
  { code: 'diagnosis', title: 'Diagnosis' },
  { code: 'diagnosis_icd', title: 'ICD Diagnosis' },
  { code: 'sedation', title: 'Sedation' },
  { code: 'treatment', title: 'Treatment' },
  {
    code: 'antibacterial_therapy_prescribed',
    title: 'Antibacterial Therapy Prescribed',
  },
  {
    code: 'number_of_days_of_ab_therapy',
    title: 'Number of Days of AB Therapy',
  },
  { code: 'recommendations_to_patient', title: 'Recommendations to Patient' },
  {
    code: 'recommendations_for_administrators_and_coordinators',
    title: 'Recommendations (for administrators and coordinators)',
  },
  { code: 'comment', title: 'Comment' },
  {
    code: 'duration_of_next_appointment',
    title: 'Duration of Next Appointment',
  },
];

const medicalSections: TemplateSectionDefinition[] = [
  {
    code: 'patient_name',
    supercategory: 'General Information',
    title: 'Patient Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'patient_date_of_birth',
    supercategory: 'General Information',
    title: 'Patient Date of Birth',
    instructions:
      "ISO format. Empty string if not mentioned. For example '1990-01-01' or ''",
  },
  {
    code: 'doctor_name',
    supercategory: 'General Information',
    title: 'Doctor Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'patient_representative_name',
    supercategory: 'General Information',
    title: 'Patient Representative Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'patient_problem',
    supercategory: 'Complaints',
    title: 'Patient Problem',
  },
  {
    code: 'patient_idea_of_treatment',
    supercategory: 'Complaints',
    title: 'Patient Idea of Treatment',
  },
  {
    code: 'patient_expectation_of_treatment',
    supercategory: 'Complaints',
    title: 'Patient Expectation of Treatment',
  },
  {
    code: 'patient_concerns_of_treatment',
    supercategory: 'Complaints',
    title: 'Patient Concerns of Treatment',
  },
  {
    code: 'patient_feelings',
    supercategory: 'Complaints',
    title: 'Patient Feelings',
  },
  {
    code: 'facts_that_may_affect_treatment',
    supercategory: 'Complaints',
    title: 'Facts That May Affect Treatment',
  },
  { code: 'life_and_disease_history', title: 'Life and Disease History' },
  { code: 'epidemiological_history', title: 'Epidemiological History' },
  { code: 'bad_habits', title: 'Bad Habits' },
  { code: 'height_cm', title: 'Height (cm)' },
  { code: 'weight_kg', title: 'Weight (kg)' },
  { code: 'bmi', title: 'Body Mass Index (BMI)' },
  { code: 'general_examination', title: 'General Examination' },
  { code: 'laboratory_referrals', title: 'Laboratory Referrals' },
  { code: 'instrumental_referrals', title: 'Instrumental Referrals' },
  { code: 'specialist_referrals', title: 'Specialist Referrals' },
  { code: 'exam_results', title: 'Exam Results' },
  { code: 'diagnosis_icd', title: 'ICD Diagnosis' },
  { code: 'clinical_diagnosis', title: 'Clinical Diagnosis' },
  { code: 'diagnosis_justification', title: 'Diagnosis Justification' },
  { code: 'general_recommendations', title: 'General Recommendations' },
  { code: 'vaccination', title: 'Vaccination' },
  { code: 'nutrition_recommendations', title: 'Nutrition Recommendations' },
  { code: 'non_medication_therapy', title: 'Non-Medication Therapy' },
  { code: 'medication_therapy', title: 'Medication Therapy' },
  { code: 'antibacterial_therapy', title: 'Antibacterial Therapy' },
  {
    code: 'red_flags',
    title: 'Red Flags (symptoms requiring urgent medical attention)',
  },
  { code: 'ebm_standards', title: 'Evidence-Based Medicine Standards (links)' },
  { code: 'comment', title: 'Comment' },
  { code: 'disability_leave', title: 'Disability Certificate / Sick Leave' },
  { code: 'follow_up_examination', title: 'Follow-up Examination' },
];

const pediatricSections: TemplateSectionDefinition[] = [
  {
    code: 'patient_name',
    supercategory: 'General Information',
    title: 'Patient Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'patient_date_of_birth',
    supercategory: 'General Information',
    title: 'Patient Date of Birth',
    instructions:
      "ISO format. Empty string if not mentioned. For example '1990-01-01' or ''",
  },
  {
    code: 'doctor_name',
    supercategory: 'General Information',
    title: 'Doctor Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'patient_representative_name',
    supercategory: 'General Information',
    title: 'Patient Representative Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'patient_problem',
    supercategory: 'Complaints',
    title: 'Patient Problem',
  },
  {
    code: 'patient_idea_of_treatment',
    supercategory: 'Complaints',
    title: 'Patient Idea of Treatment',
  },
  {
    code: 'patient_expectation_of_treatment',
    supercategory: 'Complaints',
    title: 'Patient Expectation of Treatment',
  },
  {
    code: 'patient_concerns_of_treatment',
    supercategory: 'Complaints',
    title: 'Patient Concerns of Treatment',
  },
  {
    code: 'patient_feelings',
    supercategory: 'Complaints',
    title: 'Patient Feelings',
  },
  {
    code: 'facts_that_may_affect_treatment',
    supercategory: 'Complaints',
    title: 'Facts That May Affect Treatment',
  },
  { code: 'infectious_contact', title: 'Contact with Infectious Patients' },
  { code: 'life_and_disease_history', title: 'Life and Disease History' },
  { code: 'height_cm', title: 'Height (cm)' },
  { code: 'weight_kg', title: 'Weight (kg)' },
  { code: 'objective_exam_data', title: 'Objective Examination Data' },
  { code: 'development_features', title: 'Developmental Features' },
  { code: 'allergies_intolerances', title: 'Allergies/Intolerances' },
  { code: 'diagnosis_icd', title: 'ICD Diagnosis' },
  { code: 'diagnosis', title: 'Diagnosis' },
  { code: 'diagnosis_justification', title: 'Diagnosis Justification' },
  { code: 'vaccination_done', title: 'Vaccination Done' },
  { code: 'vaccination_plan', title: 'Vaccination Plan' },
  { code: 'instrumental_referrals', title: 'Instrumental Referrals' },
  { code: 'laboratory_referrals', title: 'Laboratory Referrals' },
  { code: 'specialist_referrals', title: 'Specialist Referrals' },
  { code: 'exam_results', title: 'Exam Results' },
  { code: 'medication_therapy', title: 'Medication Therapy' },
  { code: 'general_recommendations', title: 'General Recommendations' },
  { code: 'nutrition_recommendations', title: 'Nutrition Recommendations' },
  {
    code: 'red_flags',
    title: 'Red Flags (symptoms requiring urgent medical attention)',
  },
  { code: 'ebm_standards', title: 'Evidence-Based Medicine Standards (links)' },
  { code: 'disability_leave', title: 'Disability Certificate / Sick Leave' },
  { code: 'follow_up_visit', title: 'Follow-up Visit' },
];

const coordinatorNewPatientSections: TemplateSectionDefinition[] = [
  {
    code: 'patient_name',
    supercategory: 'General Information',
    title: 'Patient Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'patient_date_of_birth',
    supercategory: 'General Information',
    title: 'Patient Date of Birth',
    instructions:
      "ISO format. Empty string if not mentioned. For example '1990-01-01' or ''",
  },
  {
    code: 'doctor_name',
    supercategory: 'General Information',
    title: 'Doctor Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'patient_representative_name',
    supercategory: 'General Information',
    title: 'Patient Representative Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'introduction_rapport',
    supercategory: 'Communication',
    title: 'Introduction & Rapport',
    instructions:
      'How did the consultation begin? Was rapport established, and how? Include specific details about the initial interaction.',
  },
  {
    code: 'patient_needs_goals',
    supercategory: 'Assessment',
    title: 'Patient Needs/Goals',
    instructions:
      "What were the patient's main concerns, needs, or goals? Include all mentioned objectives and desires.",
  },
  {
    code: 'problem_assessment',
    supercategory: 'Assessment',
    title: 'Problem Assessment',
    instructions:
      'What dental or personal problems were identified? How thoroughly were they explored? Include depth of questioning and discovery process.',
  },
  {
    code: 'treatment_presentation_education',
    supercategory: 'Treatment Discussion',
    title: 'Treatment Presentation & Education',
    instructions:
      'How were treatment options explained? What patient education or visual aids were used? Include specific methods and materials.',
  },
  {
    code: 'objections_concerns',
    supercategory: 'Treatment Discussion',
    title: 'Objections & Concerns',
    instructions:
      'What questions or objections did the patient raise, and how were they addressed? Include all concerns and responses.',
  },
  {
    code: 'financial_discussion',
    supercategory: 'Financial',
    title: 'Financial Discussion',
    instructions:
      'How were costs, insurance, and payment options discussed? Include all financial topics covered.',
  },
  {
    code: 'commitment_closing',
    supercategory: 'Closing',
    title: 'Commitment & Closing',
    instructions:
      "Was a decision or next step agreed upon? What was the coordinator's approach to closing the discussion?",
  },
  {
    code: 'documentation_compliance',
    supercategory: 'Administrative',
    title: 'Documentation & Compliance',
    instructions:
      'Were patient records and legal requirements addressed? Include any documentation or compliance topics.',
  },
];

const coordinatorFollowUpSections: TemplateSectionDefinition[] = [
  {
    code: 'patient_name',
    supercategory: 'General Information',
    title: 'Patient Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'patient_date_of_birth',
    supercategory: 'General Information',
    title: 'Patient Date of Birth',
    instructions:
      "ISO format. Empty string if not mentioned. For example '1990-01-01' or ''",
  },
  {
    code: 'doctor_name',
    supercategory: 'General Information',
    title: 'Doctor Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'patient_representative_name',
    supercategory: 'General Information',
    title: 'Patient Representative Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'reconnection_rapport',
    supercategory: 'Communication',
    title: 'Reconnection & Rapport',
    instructions:
      'How did the coordinator re-engage with the patient? Was rapport or comfort addressed?',
  },
  {
    code: 'current_patient_experience',
    supercategory: 'Assessment',
    title: 'Current Patient Experience',
    instructions:
      "What feedback, experiences, or concerns from the recent visit were discussed? How was the patient's current status or needs clarified?",
  },
  {
    code: 'next_step_explanation',
    supercategory: 'Treatment Planning',
    title: 'Next Step Explanation',
    instructions:
      "How did the coordinator describe the need for the next provider or phase in the patient's journey?",
  },
  {
    code: 'trust_building_education',
    supercategory: 'Treatment Planning',
    title: 'Trust-Building & Education',
    instructions:
      'What was done to build trust, reinforce confidence in the care team, or educate the patient about the next step?',
  },
  {
    code: 'patient_questions_concerns',
    supercategory: 'Patient Interaction',
    title: 'Patient Questions & Concerns',
    instructions:
      'What questions or concerns did the patient express about moving forward, and how were these addressed?',
  },
  {
    code: 'practical_arrangements',
    supercategory: 'Administrative',
    title: 'Practical Arrangements',
    instructions:
      'How were logistics, referrals, scheduling, or instructions for the next phase handled?',
  },
  {
    code: 'commitment_closing',
    supercategory: 'Closing',
    title: 'Commitment & Closing',
    instructions:
      "Was a decision or next step agreed upon? What was the coordinator's approach to closing the discussion?",
  },
  {
    code: 'documentation_compliance',
    supercategory: 'Administrative',
    title: 'Documentation & Compliance',
    instructions:
      'Were patient records and legal requirements addressed? Include any documentation or compliance topics.',
  },
];

const soapSections: TemplateSectionDefinition[] = [
  {
    code: 'patient_name',
    supercategory: 'General Information',
    title: 'Patient Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'patient_date_of_birth',
    supercategory: 'General Information',
    title: 'Patient Date of Birth',
    instructions:
      "ISO format. Empty string if not mentioned. For example '1990-01-01' or ''",
  },
  {
    code: 'doctor_name',
    supercategory: 'General Information',
    title: 'Doctor Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'patient_representative_name',
    supercategory: 'General Information',
    title: 'Patient Representative Name',
    instructions:
      "Empty string if not mentioned. For example: 'John Doe' or ''",
  },
  {
    code: 'S1_chief_complaint',
    supercategory: 'Subjective',
    title: 'Chief Complaint',
    instructions: "Patient's primary reason for the visit, in their own words",
  },
  {
    code: 'S2_history_of_present_illness',
    supercategory: 'Subjective',
    title: 'History of Present Illness',
    instructions:
      'Detailed account of the symptoms, including onset, duration, characteristics, aggravating or alleviating factors, and associated symptoms',
  },
  {
    code: 'S3_history',
    supercategory: 'Subjective',
    title: 'History',
    instructions: 'Relevant medical, surgical, family, and social history',
  },
  {
    code: 'S4_review_of_symptoms',
    supercategory: 'Subjective',
    title: 'Review of Symptoms',
    instructions:
      "Systematic review of the patient's symptoms, covering all body systems",
  },
  {
    code: 'S5_medications',
    supercategory: 'Subjective',
    title: 'Medications',
    instructions: 'List of current medications, including dosage and frequency',
  },
  {
    code: 'S6_allergies',
    supercategory: 'Subjective',
    title: 'Allergies',
    instructions: 'Any known allergies, including reactions',
  },
  {
    code: 'O1_vital_signs',
    supercategory: 'Objective',
    title: 'Vital Signs',
    instructions:
      "Patient's vital signs, such as blood pressure, heart rate, respiratory rate, temperature, and oxygen saturation",
  },
  {
    code: 'O2_clinical_examinations',
    supercategory: 'Objective',
    title: 'Clinical Examinations',
    instructions: 'Findings from the intraoral and extraoral examination',
  },
  {
    code: 'O3_laboratory_test_results',
    supercategory: 'Objective',
    title: 'Laboratory Test Results',
    instructions: 'Results from any lab tests conducted',
  },
  {
    code: 'O4_imaging_results',
    supercategory: 'Objective',
    title: 'Imaging Results',
    instructions: 'Findings from any imaging studies performed',
  },
  {
    code: 'A1_diagnosis',
    supercategory: 'Assessment',
    title: 'Diagnosis',
    instructions: 'The condition diagnosed based on the evaluation by Doctor',
  },
  {
    code: 'A2_dental_hygiene_diagnosis',
    supercategory: 'Assessment',
    title: 'Dental Hygiene Diagnosis',
    instructions: 'Specific dental hygiene-related diagnoses, if applicable',
  },
  {
    code: 'P1_procedures_performed',
    supercategory: 'Plan',
    title: 'Procedures Performed',
    instructions: 'Details of any procedures carried out during the visit',
  },
  {
    code: 'P2_medications_given',
    supercategory: 'Plan',
    title: 'Medications Given',
    instructions: 'Medications administered or prescribed during the visit',
  },
  {
    code: 'P3_education_provided',
    supercategory: 'Plan',
    title: 'Education Provided',
    instructions:
      'Information or instructions given to the patient regarding their condition and care',
  },
  {
    code: 'P4_referrals_given',
    supercategory: 'Plan',
    title: 'Referrals Given',
    instructions:
      'Any referrals made to other healthcare providers or specialists',
  },
];

// Static template definitions — will be replaced by API-fetched data when backend is ready
export const TEMPLATE_DEFINITIONS: Record<string, TemplateDefinition> = {
  [CONSULTATION_TYPE.Dental]: {
    type: CONSULTATION_TYPE.Dental,
    sections: dentalSections,
  },
  [CONSULTATION_TYPE.Medical]: {
    type: CONSULTATION_TYPE.Medical,
    sections: medicalSections,
  },
  [CONSULTATION_TYPE.Pediatric]: {
    type: CONSULTATION_TYPE.Pediatric,
    sections: pediatricSections,
  },
  [CONSULTATION_TYPE.CoordinatorNewPatient]: {
    type: CONSULTATION_TYPE.CoordinatorNewPatient,
    sections: coordinatorNewPatientSections,
  },
  [CONSULTATION_TYPE.CoordinatorFollowUp]: {
    type: CONSULTATION_TYPE.CoordinatorFollowUp,
    sections: coordinatorFollowUpSections,
  },
  [CONSULTATION_TYPE.Soap]: {
    type: CONSULTATION_TYPE.Soap,
    sections: soapSections,
  },
};
